import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Facebook, Instagram, Youtube, ExternalLink, MessageCircle, Heart, Zap, Send, X as XIcon } from "lucide-react";
import Seo from "../components/Seo";
import api from "../lib/api";
import { toast } from "sonner";

const HANDLE = "@pushcenterva";

const SOCIALS = [
  {
    key: "facebook",
    name: "Facebook",
    handle: HANDLE,
    url: "https://www.facebook.com/share/1By8zRYFPB/?mibextid=wwXIfr",
    embedUrl:
      "https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1By8zRYFPB%2F&tabs=timeline&width=340&height=560&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=true",
    Icon: Facebook,
    accent: "from-[#1877F2]/30 via-[#1877F2]/5 to-transparent",
    iconBg: "bg-[#1877F2]",
    description: "Daily devotionals, sermon clips, and event photos. Tap follow to never miss a Sunday.",
    cta: "Follow On Facebook",
  },
  {
    key: "instagram",
    name: "Instagram",
    handle: HANDLE,
    url: "https://www.instagram.com/pushcenterva",
    Icon: Instagram,
    accent: "from-[#E4405F]/30 via-[#833AB4]/10 to-transparent",
    iconBg: "bg-gradient-to-br from-[#833AB4] via-[#E4405F] to-[#FCAF45]",
    description: "Behind-the-scenes moments, prayer drops, and reels straight from The Push family.",
    cta: "Follow On Instagram",
  },
  {
    key: "youtube",
    name: "YouTube",
    handle: HANDLE,
    url: "https://www.youtube.com/@pushcenterva",
    Icon: Youtube,
    accent: "from-[#FF0000]/30 via-[#FF0000]/5 to-transparent",
    iconBg: "bg-[#FF0000]",
    description: "Full sermons, worship sets, and live services. Subscribe and ring the bell.",
    cta: "Watch On YouTube",
  },
];

export default function ConnectWithUs() {
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [activeAlbum, setActiveAlbum] = useState("__all__");
  const [lightbox, setLightbox] = useState(null);
  const [lightboxFullUrl, setLightboxFullUrl] = useState(null);
  const [lightboxLoading, setLightboxLoading] = useState(false);

  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  const openLightbox = async (img) => {
    setLightbox(img);
    setLightboxFullUrl(null);
    setLightboxLoading(true);
    try {
      const { data } = await api.get(`/gallery/${img.id}`);
      setLightboxFullUrl(data.image_url || img.thumb_url);
    } catch {
      setLightboxFullUrl(img.thumb_url);
    } finally {
      setLightboxLoading(false);
    }
  };

  const closeLightbox = () => {
    setLightbox(null);
    setLightboxFullUrl(null);
    setLightboxLoading(false);
  };

  // Load Instagram embed script (best-effort — works if user has any public IG embeds)
  useEffect(() => {
    if (document.getElementById("ig-embed-script")) return;
    const s = document.createElement("script");
    s.id = "ig-embed-script";
    s.async = true;
    s.src = "https://www.instagram.com/embed.js";
    document.body.appendChild(s);
  }, []);

  const handleQuickConnect = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", {
        ...contactForm,
        phone: "",
        subject: "Connect With Us — Stay Updated",
      });
      toast.success("We'll be in touch! Welcome to the Push family.");
      setContactForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen" data-testid="connect-page">
      <Seo
        title="Connect With Us"
        description="Follow The Push Center on Facebook, Instagram, and YouTube (@pushcenterva). Stay connected with sermons, devotionals, and live worship."
        path="/connect"
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0">
          <img src="/images/connect-pastor.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-55 bg-zoom" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-28 lg:py-36">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5" data-testid="connect-eyebrow">
              Connect With Us
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] mb-6">
              Find Us <span className="text-electric">@pushcenterva</span> — Everywhere You Scroll.
            </h1>
            <p className="text-lg text-zinc-300 leading-relaxed max-w-2xl">
              Follow along between Sundays. We drop sermon clips, live worship, devotionals, and the moments that remind us — God is moving at The Push.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SOCIAL CARDS GRID */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="grid md:grid-cols-3 gap-5" data-testid="social-cards-grid">
            {SOCIALS.map((s, i) => (
              <motion.a
                key={s.key}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`social-card-${s.key}`}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 hover:border-electric/40 hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${s.accent} opacity-60 group-hover:opacity-100 transition-opacity pointer-events-none`} aria-hidden="true" />
                <div className="relative">
                  <div className={`w-14 h-14 rounded-2xl ${s.iconBg} flex items-center justify-center mb-6 shadow-lg`}>
                    <s.Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="font-display font-black text-3xl tracking-tight mb-1">{s.name}</div>
                  <div className="text-electric font-bold text-sm mb-4">{s.handle}</div>
                  <p className="text-sm text-zinc-300 leading-relaxed mb-7">{s.description}</p>
                  <span className="inline-flex items-center gap-2 text-electric font-bold text-xs uppercase tracking-wider group-hover:gap-3 transition-all">
                    {s.cta}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST FROM YOUTUBE */}
      <section className="py-20 lg:py-28 border-t border-white/5 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">Latest On YouTube</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                Catch Up On <span className="text-electric">Recent Services</span>
              </h2>
            </div>
            <a
              href="https://www.youtube.com/@pushcenterva"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-electric text-black px-5 py-3 rounded-full font-bold text-xs uppercase tracking-wider hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all"
              data-testid="youtube-cta"
            >
              <Youtube className="w-4 h-4" /> Subscribe
            </a>
          </div>

          <div className="rounded-3xl overflow-hidden border border-white/10 bg-black shadow-2xl shadow-black/50" data-testid="youtube-embed-wrap">
            <div className="aspect-video">
              <iframe
                title="The Push Center YouTube Channel"
                src="https://www.youtube.com/embed?listType=user_uploads&list=pushcenterva"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FACEBOOK PAGE PLUGIN */}
      <section className="py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-2">
              <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">From The Feed</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-6">
                Live From <span className="text-electric">Facebook</span>
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-6">
                Real-time updates, devotional drops, and event flyers. Hit follow on our page and you'll never miss what God is doing.
              </p>
              <ul className="space-y-3 text-sm text-zinc-300 mb-8" data-testid="facebook-bullets">
                <li className="flex items-start gap-3"><Heart className="w-4 h-4 text-electric mt-1 flex-shrink-0" aria-hidden="true" /> Weekly worship moments and reels</li>
                <li className="flex items-start gap-3"><MessageCircle className="w-4 h-4 text-electric mt-1 flex-shrink-0" aria-hidden="true" /> Pastor's mid-week encouragements</li>
                <li className="flex items-start gap-3"><Zap className="w-4 h-4 text-electric mt-1 flex-shrink-0" aria-hidden="true" /> Event announcements & livestream alerts</li>
              </ul>
              <a
                href="https://www.facebook.com/share/1By8zRYFPB/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#1877F2] text-white px-5 py-3 rounded-full font-bold text-xs uppercase tracking-wider hover:-translate-y-0.5 transition-all"
                data-testid="facebook-cta"
              >
                <Facebook className="w-4 h-4" /> Visit Our Page
              </a>
            </div>
            <div className="lg:col-span-3" data-testid="facebook-embed-wrap">
              <div className="rounded-3xl overflow-hidden border border-white/10 bg-[#0a0a0a] p-2">
                <iframe
                  title="The Push Center Facebook"
                  src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1By8zRYFPB%2F&tabs=timeline&width=500&height=560&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true"
                  width="100%"
                  height="560"
                  style={{ border: "none", overflow: "hidden", borderRadius: "1rem" }}
                  scrolling="no"
                  allow="encrypted-media"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSTAGRAM CTA */}
      <section className="py-20 lg:py-28 border-t border-white/5 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <div className="rounded-3xl border border-white/10 overflow-hidden relative">
            <div className="absolute inset-0">
              <img src="/images/ministries-worship.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/40" />
            </div>
            <div className="relative p-10 lg:p-14 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">Latest On Instagram</div>
                <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-5">
                  Tag Us <span className="text-electric">@pushcenterva</span>
                </h2>
                <p className="text-zinc-300 leading-relaxed mb-7">
                  Share your worship moment, your sermon notes, your testimony. Use <strong className="text-white">#OutOfComplacency</strong> and we'll re-share the moves of God.
                </p>
                <a
                  href="https://www.instagram.com/pushcenterva"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-br from-[#833AB4] via-[#E4405F] to-[#FCAF45] text-white px-5 py-3 rounded-full font-bold text-xs uppercase tracking-wider hover:-translate-y-0.5 transition-all"
                  data-testid="instagram-cta"
                >
                  <Instagram className="w-4 h-4" /> Follow On Instagram
                </a>
              </div>
              <div className="grid grid-cols-2 gap-3" data-testid="instagram-tiles">
                <a href="https://www.instagram.com/pushcenterva" target="_blank" rel="noopener noreferrer" className="aspect-square rounded-2xl overflow-hidden group relative">
                  <img src="/images/pastors-laughing.jpg" alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <Instagram className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </a>
                <a href="https://www.instagram.com/pushcenterva" target="_blank" rel="noopener noreferrer" className="aspect-square rounded-2xl overflow-hidden group relative">
                  <img src="/images/ministries-worship.jpg" alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <Instagram className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </a>
                <a href="https://www.instagram.com/pushcenterva" target="_blank" rel="noopener noreferrer" className="aspect-square rounded-2xl overflow-hidden group relative">
                  <img src="/images/blog-pastor.jpg" alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <Instagram className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </a>
                <a href="https://www.instagram.com/pushcenterva" target="_blank" rel="noopener noreferrer" className="aspect-square rounded-2xl overflow-hidden group relative">
                  <img src="/images/connect-pastor.jpg" alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <Instagram className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {gallery.length > 0 && (() => {
        const albums = Array.from(new Set(gallery.map((g) => g.album).filter(Boolean))).sort();
        const visible = activeAlbum === "__all__" ? gallery : gallery.filter((g) => g.album === activeAlbum);
        return (
        <section className="py-20 lg:py-28 border-t border-white/5" data-testid="gallery-section">
          <div className="max-w-7xl mx-auto px-5 lg:px-10">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">From The Push</div>
                <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                  Moments <span className="text-electric">Captured</span>
                </h2>
                <p className="text-zinc-400 mt-3 max-w-xl">A look inside our services, ministry events, and the family God is building.</p>
              </div>
              <div className="text-xs uppercase tracking-wider text-zinc-500" data-testid="gallery-count">
                {visible.length} {visible.length === 1 ? "Photo" : "Photos"}
              </div>
            </div>

            {albums.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8" role="tablist" aria-label="Photo albums" data-testid="album-tabs">
                <button
                  onClick={() => setActiveAlbum("__all__")}
                  role="tab"
                  aria-selected={activeAlbum === "__all__"}
                  data-testid="album-tab-all"
                  className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border transition ${
                    activeAlbum === "__all__" ? "border-electric bg-electric text-black" : "border-white/10 text-zinc-300 hover:border-electric/40 hover:text-white"
                  }`}
                >
                  All Photos
                </button>
                {albums.map((a) => (
                  <button
                    key={a}
                    onClick={() => setActiveAlbum(a)}
                    role="tab"
                    aria-selected={activeAlbum === a}
                    data-testid={`album-tab-${a.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border transition ${
                      activeAlbum === a ? "border-electric bg-electric text-black" : "border-white/10 text-zinc-300 hover:border-electric/40 hover:text-white"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
              {visible.map((img, i) => (
                <motion.button
                  key={img.id}
                  type="button"
                  onClick={() => openLightbox(img)}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.4) }}
                  className="group relative aspect-square overflow-hidden rounded-2xl border border-white/5 bg-[#0a0a0a] hover:border-electric/40 transition-all"
                  data-testid={`gallery-tile-${img.id}`}
                  aria-label={img.caption || "Gallery image"}
                >
                  <img
                    src={img.thumb_url || img.image_url}
                    alt={img.caption || ""}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {(img.caption || img.album) && (
                    <div className="absolute inset-0 flex flex-col justify-end p-3 bg-gradient-to-t from-black/85 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      {img.album && <span className="text-[9px] uppercase tracking-wider text-electric font-bold mb-0.5">{img.album}</span>}
                      {img.caption && <span className="text-xs text-white font-medium line-clamp-2">{img.caption}</span>}
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        </section>
        );
      })()}

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.caption || "Gallery image"}
          onClick={closeLightbox}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in"
          data-testid="gallery-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            aria-label="Close"
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-electric hover:text-black text-white flex items-center justify-center transition"
            data-testid="lightbox-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
          <div className="max-w-5xl max-h-[85vh] w-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              {/* Show the small thumb instantly as a backdrop while the full-res loads */}
              <img
                src={lightboxFullUrl || lightbox.thumb_url}
                alt={lightbox.caption || ""}
                className="max-h-[80vh] w-auto object-contain rounded-xl"
              />
              {lightboxLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl">
                  <div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            {lightbox.caption && <p className="text-center text-zinc-300 text-sm max-w-2xl">{lightbox.caption}</p>}
          </div>
        </div>
      )}

      {/* QUICK CONNECT FORM */}
      <section className="py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 text-center">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">Stay In The Loop</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-4">
            Don't Use Social? <span className="text-electric">We've Got You.</span>
          </h2>
          <p className="text-zinc-300 leading-relaxed mb-10">
            Drop your info and we'll keep you posted on services, special events, and the moments that matter.
          </p>
          <form onSubmit={handleQuickConnect} className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto" data-testid="connect-form">
            <input
              required
              type="text"
              placeholder="Your Name"
              value={contactForm.name}
              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              className="px-4 py-3.5 rounded-lg bg-[#0a0a0a] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric"
              data-testid="connect-form-name"
            />
            <input
              required
              type="email"
              placeholder="Your Email"
              value={contactForm.email}
              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              className="px-4 py-3.5 rounded-lg bg-[#0a0a0a] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric"
              data-testid="connect-form-email"
            />
            <textarea
              rows={3}
              placeholder="Tell us a little about you (optional)"
              value={contactForm.message}
              onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
              className="sm:col-span-2 px-4 py-3.5 rounded-lg bg-[#0a0a0a] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric"
              data-testid="connect-form-message"
            />
            <button
              type="submit"
              disabled={sending}
              className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold text-sm uppercase tracking-wider hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all disabled:opacity-60"
              data-testid="connect-form-submit"
            >
              <Send className="w-4 h-4" /> {sending ? "Sending..." : "Stay Connected"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
